import { useRef } from 'react';
import { useGetGuestSiteScheduleQuery } from './ScheduleList.graphql.generated';
import { useGetProScheduleListQuery } from './ScheduleListPro.graphql.generated';
import type { GetProScheduleListQuery } from './ScheduleListPro.graphql.generated';
import type { GetGuestSiteScheduleQuery } from './ScheduleList.graphql.generated';
import type { ScheduleListData } from './ScheduleList.types';
import { useEventUserRole } from '@shared/components/AuthProvider';
import { useIsCurrentPageSsrFetchEnabled } from '@apps/guest/shared/hooks/useIsCurrentPageSsrFetchEnabled';
import { useEventInfo } from '@shared/utils/eventInfo';
import { EventType } from '@graphql/generated-base';
import { usesFirebaseScheduleBackend } from '@shared/components/GeneralSchedule/usesFirebaseScheduleBackend';
import queryString from '@shared/core/queryString';

// Use the explicit ?preview=true query param rather than isInIframe() — iframes can appear
// in other legitimate contexts (e.g. masked/custom domains) that are not preview sessions.
const isPreviewing = queryString.getValueString('preview') === 'true';

const extractProData = (raw: GetProScheduleListQuery | undefined): ScheduleListData | undefined => {
  const schedule = raw?.getGuestGeneralEventScheduleForEvent;
  if (!schedule) return undefined;
  return {
    formattedSchedules: schedule.formattedSchedules,
    toBeAnnouncedItems: schedule.tbaSchedules,
    activeSession: raw?.eventByName?.activeSession ?? undefined,
  };
};

const extractWeddingData = (raw: GetGuestSiteScheduleQuery | undefined): ScheduleListData | undefined => {
  const eventByName = raw?.eventByName;
  const schedule = eventByName?.info?.schedule;
  if (!schedule) return undefined;
  return {
    formattedSchedules: schedule.formattedSchedules,
    toBeAnnouncedItems: schedule.toBeAnnouncedItems,
    activeSession: eventByName?.activeSession ?? undefined,
  };
};

/**
 * In preview, always pass isAdmin:true in the query variables so Apollo never registers
 * an ObservableQuery with isAdmin:false. Even with skip:true, Apollo tracks the
 * ObservableQuery and client.refetchQueries will force-refetch it with those variables.
 * Security: skipUntilAdminResolved prevents the query from executing until the server
 * has confirmed admin status — a guest spoofing ?preview=true stays permanently skipped.
 */
const usePreviewAdminQuery = (isAdmin: boolean) => {
  const isAdminRef = useRef(isAdmin);
  if (isAdmin) isAdminRef.current = true;
  return {
    skipUntilAdminResolved: isPreviewing && !isAdminRef.current,
    queryIsAdmin: isPreviewing ? true : isAdmin,
  };
};

export const useFetchScheduleListData = (eventHandle: string, skip?: boolean) => {
  const { isAdmin } = useEventUserRole();
  const { eventInfo, ssrEventInfo } = useEventInfo();
  const isSsrEnabled = useIsCurrentPageSsrFetchEnabled(eventHandle, 'schedule');
  const { skipUntilAdminResolved, queryIsAdmin } = usePreviewAdminQuery(isAdmin);

  const eventId = eventInfo?.eventId ?? '';
  const resolvedEventType = eventInfo?.eventType || ssrEventInfo?.eventType;
  const eventTypeResolved = resolvedEventType != null;
  const isProEvent = resolvedEventType === EventType.general;
  const usesWeddingQuery = usesFirebaseScheduleBackend(resolvedEventType);

  // Pro query — fires only for pro/general events once event type is known
  const {
    data: proData,
    previousData: proPreviousData,
    loading: proLoading,
    refetch: proRefetch,
  } = useGetProScheduleListQuery({
    variables: { eventId, eventHandle },
    batchMode: 'fast',
    ssr: isSsrEnabled,
    skip: skip || !eventTypeResolved || !isProEvent || !eventId,
  });

  // Wedding query — fires for wedding and baby registry events; skipped once we know it's a pro event
  const {
    data: weddingData,
    previousData: weddingPreviousData,
    loading: weddingLoading,
    refetch: weddingRefetch,
  } = useGetGuestSiteScheduleQuery({
    variables: { eventHandle, isAdmin: queryIsAdmin },
    batchMode: 'fast',
    ssr: isSsrEnabled,
    skip: skip || skipUntilAdminResolved || (eventTypeResolved && !usesWeddingQuery),
  });

  if (isProEvent) {
    return {
      data: extractProData(proData),
      previousData: extractProData(proPreviousData),
      loading: proLoading,
      refetch: proRefetch,
    };
  } else {
    return {
      data: extractWeddingData(weddingData),
      previousData: extractWeddingData(weddingPreviousData),
      loading: weddingLoading,
      refetch: weddingRefetch,
    };
  }
};
